/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable react/jsx-max-depth */
import { Loader } from '@transferwise/components';
import { formatMoney } from '@transferwise/formatting';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import autoBind from 'react-autobind';
import { InView } from 'react-intersection-observer';
import { FormattedMessage, injectIntl } from 'react-intl';

import CurrencyRouteDescription from '../../common/CurrencyRouteDescription';
import Disclaimer from '../../common/Disclaimer';
import ErrorTemplate from '../../common/ErrorTemplate';
import ShowMoreProviders from '../../common/ShowMoreProviders';
import { ComparisonPages } from '../../common/domain/ComparisonPages';
import injectTheme from '../../common/injectTheme';
import { REVOLUT_WITH_DISCLAIMER } from '../../common/providerDisclaimers/ProviderWithDisclaimer';
import { TapestryCard } from '../../common/tapestrycard/TapestryCard';
import ConditionalWrapper from '../../resources/ConditionalWrapper';
import calculateDifferenceWithTwoDecimalPlaces from '../../resources/calculateDifferenceWithTwoDecimalPlaces';
import { isDeliveryEstimationCollectedForAllProviders } from '../../resources/deliveryEstimationService';
import filterQuotes from '../../resources/filterQuotes';
import isWisePresentAndNotCheapest from '../../resources/isWisePresentAndNotCheapest';
import sortQuotesByReceivedAmount from '../../resources/sortQuotesByReceivedAmount';
import style from '../../resources/static/less/comparison-components.module.less';
import { trackTableViewed, getTrackingTriggerIndex } from '../../resources/tracking';
import { Table } from '../../resources/tracking/types';

import Provider from './atoms/Provider';

function createClassNames(classNames) {
  return classNames.map((className) => `${style[className] || className}`).join(' ');
}

function applyLimitToQuotes(quotes, limitQuotes, maxVisibleProviders) {
  if (limitQuotes && quotes.length >= maxVisibleProviders) {
    return quotes.slice(0, maxVisibleProviders);
  }
  return quotes;
}

function addDeliveryEstimation(isScreenModeDark) {
  return (
    // eslint-disable-next-line sonarjs/no-duplicate-string
    <th
      className={createClassNames([
        'table-plain__th',
        isScreenModeDark ? 'table-plain__th--dark' : '',
      ])}
    >
      <FormattedMessage id="should-arrive" />
    </th>
  );
}

function calculateHighestReceivedAmount(quotes, excludePartners = false) {
  const sortedQuotes = sortQuotesByReceivedAmount(quotes, excludePartners);
  return sortedQuotes.length > 0 ? sortedQuotes[0].receivedAmount : 0;
}

function calculateCentreProviderIndex(quotes) {
  return quotes ? getTrackingTriggerIndex(quotes.length) : null;
}

function addCustomTableFooterToVsPage(PROVIDERS_WITH_DISCLAIMERS, page, sourceCurrency, quotes) {
  if (page !== ComparisonPages.VERSUS_PAGE || sourceCurrency !== 'GBP') {
    return null;
  }

  const otherProvider = quotes.find((quote) => quote.alias !== 'wise');
  return otherProvider
    ? PROVIDERS_WITH_DISCLAIMERS.find(
        (provider) =>
          provider.alias === otherProvider.alias && provider.comparisonTableFooterDisclaimer,
      )?.comparisonTableFooterDisclaimer
    : null;
}

class ComparisonTable extends Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      limitQuotes: true,
      centreProviderViewed: false,
    };
  }

  componentDidUpdate(previousProps, previousState, snapshot) {
    const { onError, error, errorType, sourceCurrency, targetCurrency, amount } = this.props;
    const context = { ...this.props, ...this.state };

    if (!previousProps.error && error) {
      onError(errorType, context);
    }

    if (
      previousProps.sourceCurrency !== sourceCurrency ||
      previousProps.targetCurrency !== targetCurrency ||
      previousProps.amount !== amount
    ) {
      this.setState({
        centreProviderViewed: false,
      });
    }
  }

  onCallToActionClick() {
    const { onCallToActionClick } = this.props;
    const context = { ...this.props, ...this.state };
    onCallToActionClick(context);
  }

  onClickToSeePricingDisclaimer(targetCountry, providerAlias) {
    const { onClickToSeePricingDisclaimer } = this.props;
    const context = { ...this.props, ...this.state };
    onClickToSeePricingDisclaimer(targetCountry, providerAlias, context);
  }

  onClickToSeeDisclaimer() {
    const { onClickToSeeDisclaimer } = this.props;
    const context = { ...this.props, ...this.state };
    onClickToSeeDisclaimer(context);
  }

  onProviderClick(providerName) {
    const { onProviderClick } = this.props;
    const context = { ...this.props, ...this.state };
    onProviderClick(providerName, context);
  }

  onClickingDeliveryEstimationTooltip() {
    const { onClickingDeliveryEstimationTooltip } = this.props;
    const context = { ...this.props, ...this.state };
    onClickingDeliveryEstimationTooltip(context);
  }

  toggleLimitQuotes() {
    this.setState((state) => ({ limitQuotes: !state.limitQuotes }));
  }

  onTableView = (inView, quotes) => {
    if (!inView) {
      return;
    }

    this.setState({
      centreProviderViewed: true,
    });

    const { amount, sourceCurrency, targetCurrency, trackEvent } = this.props;
    const trackingContext = {
      amount,
      source: sourceCurrency,
      target: targetCurrency,
      quotes,
      componentType: Table.Educational,
    };

    trackTableViewed(trackingContext, trackEvent);
  };

  render() {
    const {
      amount,
      midMarketRate: midMarketRateAndRoute,
      quotes,
      sourceCurrency,
      targetCurrency,
      loadingQuotes,
      loadingRates,
      maxVisibleProviders,
      expandDisclaimer,
      error,
      errorType,
      excludePartners,
      deliveryEstimationEnabled,
      showSendMoneyButton,
      intl,
      providerLogoRedirectEnabled,
      isScreenModeDark,
      showMoreProvidersOption,
      page,
    } = this.props;
    const currentLocale = intl.locale;

    const showDeliveryEstimation =
      deliveryEstimationEnabled && isDeliveryEstimationCollectedForAllProviders(quotes);

    // TODO Renaming here to avoid confusion on usage below. This needs a wider refactor further up the stack
    const midMarketRate = midMarketRateAndRoute
      ? midMarketRateAndRoute.rate
      : midMarketRateAndRoute;

    const { limitQuotes, centreProviderViewed } = this.state;

    const highestReceivedAmount = calculateHighestReceivedAmount(quotes, excludePartners);

    const filteredQuotes = filterQuotes(quotes, { excludePartners });
    const sortedQuotes = sortQuotesByReceivedAmount(filteredQuotes);
    const limitedQuotes = applyLimitToQuotes(sortedQuotes, limitQuotes, maxVisibleProviders);
    const centreProviderIndex = calculateCentreProviderIndex(limitedQuotes);

    const shouldShowFooter = showMoreProvidersOption || isWisePresentAndNotCheapest(quotes);
    const PROVIDERS_WITH_DISCLAIMERS = [REVOLUT_WITH_DISCLAIMER];

    const ProviderRow = React.forwardRef((props, reference) => {
      const { quote, isCentre } = props;

      return (
        <Provider
          alias={quote.alias}
          amount={quote.receivedAmount}
          createClassNames={createClassNames}
          differenceToCheapest={calculateDifferenceWithTwoDecimalPlaces(
            highestReceivedAmount,
            quote.receivedAmount,
          )}
          fees={quote.fees}
          highestReceivedAmount={quote.receivedAmount === highestReceivedAmount}
          key={quote.id}
          logo={quote.logos.normal.svgUrl}
          logoAltText={quote.logos.altText ? quote.logos.altText : quote.name}
          name={quote.name}
          providerRate={quote.rate}
          midMarketRate={midMarketRate}
          deliveryEstimation={quote.deliveryEstimation}
          showDeliveryEstimation={showDeliveryEstimation}
          source={sourceCurrency}
          styles={style}
          target={targetCurrency}
          targetCountry={quote.targetCountry}
          isPartner={quote.partner}
          showSendMoneyButton={showSendMoneyButton}
          onCallToActionClick={this.onCallToActionClick}
          onClickToSeePricingDisclaimer={this.onClickToSeePricingDisclaimer}
          onClickingDeliveryEstimationTooltip={this.onClickingDeliveryEstimationTooltip}
          providerLogoRedirectEnabled={providerLogoRedirectEnabled}
          onProviderClick={this.onProviderClick}
          isCentre={isCentre}
          isScreenModeDark={isScreenModeDark}
          ref={reference}
          providersWithDisclaimers={PROVIDERS_WITH_DISCLAIMERS}
        />
      );
    });

    return (
      <div className={style['comparison-table']}>
        <TapestryCard screenMode={isScreenModeDark ? 'dark' : 'light'}>
          <div className={createClassNames(['card-table'])}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loadingQuotes || loadingRates ? (
              <WrappedLoader />
            ) : error ? (
              <ErrorTemplate
                createClassNames={createClassNames}
                style={style}
                errorType={errorType}
              />
            ) : (
              <>
                <div>
                  <table className={createClassNames(['table-plain'])}>
                    <thead className={createClassNames(['thead'])}>
                      <tr>
                        <th
                          className={createClassNames([
                            'table-plain__th',
                            isScreenModeDark ? 'table-plain__th--dark' : '',
                          ])}
                        >
                          <FormattedMessage id="provider" />
                        </th>
                        {showDeliveryEstimation ? addDeliveryEstimation(isScreenModeDark) : ''}
                        <th
                          className={createClassNames([
                            'table-plain__th',
                            isScreenModeDark ? 'table-plain__th--dark' : '',
                          ])}
                        >
                          <FormattedMessage id="exchange-rate" />
                          <span className={style.headingInfo}>
                            <CurrencyRouteDescription
                              sourceCurrency={sourceCurrency}
                              targetCurrency={targetCurrency}
                              exchangeRate={midMarketRate}
                              styles={style}
                            />
                          </span>
                        </th>
                        <th
                          className={createClassNames([
                            'table-plain__th',
                            isScreenModeDark ? 'table-plain__th--dark' : '',
                          ])}
                        >
                          <FormattedMessage id="transfer-fee" />
                        </th>
                        <th
                          className={createClassNames([
                            'table-plain__th',
                            'featured--th',
                            isScreenModeDark ? 'table-plain__th--dark' : '',
                          ])}
                        >
                          <FormattedMessage id="you-receive" />
                          <span
                            className={createClassNames([
                              'table-plain__th--small',
                              isScreenModeDark ? 'table-plain__th--dark' : '',
                            ])}
                          >
                            <FormattedMessage
                              values={{
                                amountWithCurrency: formatMoney(
                                  amount,
                                  sourceCurrency,
                                  currentLocale,
                                ),
                              }}
                              id="sending-amount"
                            />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className={style.tbody}>
                      {limitedQuotes.map((quote, index) => {
                        const isCentreProvider = index === centreProviderIndex;
                        return (
                          // eslint-disable-next-line react/jsx-key
                          <ConditionalWrapper
                            key={quote.alias}
                            condition={!centreProviderViewed && isCentreProvider}
                            wrapper={(children) => (
                              <InView
                                onChange={(inView, _) => this.onTableView(inView, limitedQuotes)}
                                threshold={1}
                                triggerOnce
                              >
                                {({ _, ref }) =>
                                  React.cloneElement(children, {
                                    ref: ref,
                                    isCentre: true,
                                  })
                                }
                              </InView>
                            )}
                          >
                            <ProviderRow quote={quote} />
                          </ConditionalWrapper>
                        );
                      })}
                    </tbody>
                    {((quotes.length > maxVisibleProviders && !error) ||
                      isWisePresentAndNotCheapest(excludePartners ? filteredQuotes : quotes)) && (
                      <tfoot
                        className={`${style.tfoot} ${isScreenModeDark ? style['tfoot--dark'] : ''}`}
                      >
                        {shouldShowFooter && (
                          <tr>
                            <td
                              className={createClassNames([
                                'alert',
                                'alert-info',
                                isScreenModeDark ? 'alert--dark' : '',
                              ])}
                              role="alert"
                            >
                              {isWisePresentAndNotCheapest(
                                excludePartners ? filteredQuotes : quotes,
                              ) && (
                                <span data-testid="competitor-is-cheaper">
                                  <CompetitorIsCheaperMessage
                                    deliveryEstimationShown={showDeliveryEstimation}
                                  />
                                </span>
                              )}
                              {showMoreProvidersOption &&
                                quotes.length > maxVisibleProviders &&
                                !error && (
                                  <div className="text-xs-center">
                                    <ShowMoreProviders
                                      isExpanded={!limitQuotes}
                                      onToggle={this.toggleLimitQuotes}
                                    />
                                  </div>
                                )}
                            </td>
                          </tr>
                        )}
                      </tfoot>
                    )}
                  </table>
                </div>
              </>
            )}
          </div>
        </TapestryCard>
        <Disclaimer
          createClassNames={createClassNames}
          expandDisclaimer={expandDisclaimer}
          style={style}
          onClickToSeeDisclaimer={this.onClickToSeeDisclaimer}
          sourceCurrency={sourceCurrency}
          targetCurrency={targetCurrency}
          customFooterDisclaimer={addCustomTableFooterToVsPage(
            PROVIDERS_WITH_DISCLAIMERS,
            page,
            sourceCurrency,
            quotes,
          )}
        />
      </div>
    );
  }
}

// TODO Disabling linting for now as the passed props themselves need a refactor
/* eslint-disable react/require-default-props */
ComparisonTable.propTypes = {
  amount: PropTypes.number.isRequired,
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,

  // eslint-disable-next-line react/require-default-props
  // TODO This is really confusingly named, refactor
  midMarketRate: PropTypes.shape({
    rate: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
  }), // Probably should be required, but as could be async today w/ WithComparisonResources, its optional

  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      alias: PropTypes.string.isRequired,
      logos: PropTypes.shape({
        normal: PropTypes.shape({
          svgUrl: PropTypes.string,
          pngUrl: PropTypes.string,
        }),
        altText: PropTypes.string.isRequired,
      }),
      rate: PropTypes.number.isRequired,
      fees: PropTypes.number.isRequired,
      receivedAmount: PropTypes.number.isRequired,
      dateCollected: PropTypes.string.isRequired,
      deliveryEstimation: PropTypes.shape({
        duration: PropTypes.shape({
          min: PropTypes.string,
          max: PropTypes.string,
        }),
        providerGivesEstimate: PropTypes.bool,
      }),
    }),
  ), // Probably should be required, but as could be async today w/ WithComparisonResources, its optional

  loadingRates: PropTypes.bool.isRequired,
  loadingQuotes: PropTypes.bool.isRequired,

  maxVisibleProviders: PropTypes.number,
  expandDisclaimer: PropTypes.bool,

  excludePartners: PropTypes.bool,

  error: PropTypes.bool,
  errorType: PropTypes.oneOf(['UNSUPPORTED_ROUTE', 'EXCEPTION']),

  onError: PropTypes.func,
  showSendMoneyButton: PropTypes.bool,
  onCallToActionClick: PropTypes.func,
  onClickToSeePricingDisclaimer: PropTypes.func,
  onClickToSeeDisclaimer: PropTypes.func,
  onClickingDeliveryEstimationTooltip: PropTypes.func,
  deliveryEstimationEnabled: PropTypes.bool,
  providerLogoRedirectEnabled: PropTypes.bool,
  onProviderClick: PropTypes.func,
  trackEvent: PropTypes.func,
  isScreenModeDark: PropTypes.bool,
  showMoreProvidersOption: PropTypes.bool,
  page: PropTypes.string,
  useV3ComparisonApi: PropTypes.bool,
};

ComparisonTable.defaultProps = {
  deliveryEstimationEnabled: false,
  excludePartners: false,
  showSendMoneyButton: true,
  providerLogoRedirectEnabled: false,
  onClickToSeeDisclaimer: () => {},
  onProviderClick: () => {},
  trackEvent: () => {},
  showMoreProvidersOption: true,
  useV3ComparisonApi: false,
};

// eslint-disable-next-line fp/no-mutation
ComparisonTable.componentType = Table.Educational;

const WrappedLoader = () => (
  <div className={createClassNames(['text-xs-center', 'loader', 'm-b-3', 'p-t-5', 'p-b-5'])}>
    <Loader classNames={style} />
  </div>
);

const CompetitorIsCheaperMessage = ({ deliveryEstimationShown }) => (
  <FormattedMessage
    id={deliveryEstimationShown ? 'not-cheapest-with-speed' : 'not-cheapest-without-speed'}
  />
);

CompetitorIsCheaperMessage.propTypes = {
  deliveryEstimationShown: PropTypes.bool,
};

CompetitorIsCheaperMessage.defaultProps = {
  // Based on the copy, this is the safer default as it makes less excuses
  deliveryEstimationShown: true,
};

export default injectTheme(injectIntl(ComparisonTable));
